import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { useMobileContext } from '../layouts/MobileLayout';
import MdtParacetamolModal, { type ParacetamolScreeningResult } from '../components/mobile/MdtParacetamolModal';
import { generatePARRef } from '../lib/prfUtils';
import { resolveMdtFormIncident } from '../lib/mdtFormIncident';
import { API_BASE_URL } from '../lib/utils';
import { ArrowLeft, FileText, Pill, Stethoscope } from 'lucide-react';

/**
 * PRF entry hub — uses the simplified "current" MDT action design.
 * Opened from the classic MDT home when the user presses Patient Report Form.
 */
export default function MdtFormsHubPage() {
  const navigate = useNavigate();
  const reduxDispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const { mdtResource } = useMobileContext();
  const [showParacetamolModal, setShowParacetamolModal] = useState(false);
  const [submittingParacetamol, setSubmittingParacetamol] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const resolveQuickActionLocation = async () => {
    let location = { latitude: 0, longitude: 0, address: 'Unknown' };
    try {
      if (!navigator.geolocation) return location;
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
        });
      });
      try {
        const geocodeRes = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=18&addressdetails=1`,
          { headers: { 'User-Agent': 'C2-Command-System' } }
        );
        if (geocodeRes.ok) {
          const geocodeData = await geocodeRes.json();
          const addr = geocodeData.address || {};
          const parts = [
            addr.road || addr.street || '',
            addr.suburb || addr.district || '',
            addr.town || addr.city || addr.village || '',
            addr.county || '',
            addr.postcode || '',
          ].filter(Boolean);
          location = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            address:
              parts.length > 0
                ? `GPS Location Near: ${parts.join(', ')}`
                : `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
          };
        } else {
          location = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
          };
        }
      } catch {
        location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
        };
      }
    } catch {
      /* ignore */
    }
    return location;
  };

  const handleParacetamolSubmit = async (result: ParacetamolScreeningResult) => {
    if (!token) return;
    setSubmittingParacetamol(true);
    try {
      const location = await resolveQuickActionLocation();
      const screeningSummary = [
        `Taken in last 4 hours: ${result.takenLast4Hours ? 'Yes' : 'No'}`,
        `Under 16: ${result.under16 ? 'Yes' : 'No'}`,
        `Allergic: ${result.allergic ? 'Yes' : 'No'}`,
        `GP/HCP restriction: ${result.gpRestricted ? 'Yes' : 'No'}`,
      ].join(' | ');

      const outcome = result.clearedWithoutBlock
        ? 'clear'
        : result.hcpApproved
          ? 'hcp_authorised'
          : 'blocked';

      let title = 'Paracetamol — screening clear';
      let description = `Paracetamol screening passed. Patient: ${result.fullName}. ${screeningSummary}`;
      if (result.blocked) {
        title = result.hcpApproved
          ? 'Paracetamol — HCP authorised'
          : 'Paracetamol — do not administer';
        description = [
          `Patient: ${result.fullName} | DOB: ${result.dateOfBirth} | Contact: ${result.contactNumber}`,
          screeningSummary,
          result.hcpApproved
            ? `HCP approved: ${result.hcpName} (${result.hcpAuthorisationCode})`
            : 'Blocked pending telephone authorisation or HCP override',
        ].join(' | ');
      }

      const { incidentId, eventId, createdNew } = await resolveMdtFormIncident({
        token,
        mdtResource,
        activeDispatch,
        reduxDispatch,
        createTitle: title,
        createDescription: `${description} — ${mdtResource?.name || 'MDT unit'} at ${location.address}`,
        createSeverity: result.blocked && !result.hcpApproved ? 'medium' : 'low',
        createTags: ['paracetamol', result.blocked ? 'paracetamol-blocked' : 'paracetamol-clear', 'prf'],
        location,
      });

      const parRef = generatePARRef(eventId, incidentId);
      const prfRes = await fetch(`${API_BASE_URL}/prfs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id: parRef,
          eventId,
          incidentId,
          data: {
            formType: 'paracetamol_screening',
            patientDetails: {
              firstName: result.fullName,
              dateOfBirth: result.dateOfBirth,
              phoneNumber: result.contactNumber,
            },
            presentingComplaint: {
              chiefComplaint: 'Paracetamol screening',
              additionalNotes: description,
            },
            paracetamolScreening: {
              takenLast4Hours: result.takenLast4Hours,
              under16: result.under16,
              allergic: result.allergic,
              gpRestricted: result.gpRestricted,
              blocked: result.blocked,
              hcpApproved: result.hcpApproved,
              hcpName: result.hcpName,
              hcpAuthorisationCode: result.hcpAuthorisationCode,
              outcome,
            },
            sectionStatus: {
              patientDetails: 'complete',
              nokDetails: 'not_started',
              presentingComplaint: 'complete',
              pastMedicalHistory: 'not_started',
              allergiesMeds: 'not_started',
              fast: 'not_started',
              observations: 'not_started',
              attending: 'not_started',
            },
          },
        }),
      });
      if (!prfRes.ok) throw new Error('Failed to save PRF');

      const message = result.blocked
        ? result.hcpApproved
          ? `Paracetamol saved — ${parRef}`
          : 'Paracetamol screening blocked — follow phone steps'
        : createdNew
          ? `Screening saved — ${parRef} (new incident)`
          : `Screening saved — ${parRef}`;

      setFeedback(message);
      setShowParacetamolModal(false);
      setTimeout(() => {
        navigate('/', { replace: true, state: { quickActionFeedback: message } });
      }, 1200);
    } catch (e) {
      throw e instanceof Error ? e : new Error('Failed to save screening');
    } finally {
      setSubmittingParacetamol(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-[18px] pb-4 min-h-0">
        <div className="pt-4 space-y-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/50 hover:text-white/80 transition-colors mb-1"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[11px] font-semibold">Back to MDT</span>
          </button>

          <p className="text-[8px] font-bold uppercase tracking-[1.5px] text-white/30">
            Patient Report Forms
          </p>
          <p className="text-[11px] text-white/40 -mt-1">
            Choose the form type for this patient contact
          </p>

          {feedback && (
            <div
              className="px-3 py-2 rounded-xl text-[11px] font-medium text-center"
              style={{
                background: 'rgba(96,165,250,0.12)',
                border: '1px solid rgba(96,165,250,0.2)',
                color: '#93c5fd',
              }}
            >
              {feedback}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => setShowParacetamolModal(true)}
              disabled={submittingParacetamol}
              className="py-5 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform disabled:opacity-60"
              style={{
                background: 'rgba(59,130,246,0.06)',
                border: '1.5px solid rgba(59,130,246,0.2)',
              }}
            >
              <Pill className="w-7 h-7 text-blue-400" />
              <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wide">
                Paracetamol
              </span>
              <span className="text-[9px] text-white/35 px-4 text-center">
                Screening &amp; administration record
              </span>
            </button>

            <button
              onClick={() => navigate('/minor-injury')}
              className="py-5 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
              style={{
                background: 'rgba(251,191,36,0.06)',
                border: '1.5px solid rgba(251,191,36,0.2)',
              }}
            >
              <Stethoscope className="w-7 h-7 text-amber-400" />
              <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wide">
                Minor Injury
              </span>
              <span className="text-[9px] text-white/35 px-4 text-center">
                Short form for minor injury contacts
              </span>
            </button>

            <button
              onClick={() => navigate('/prf')}
              className="py-5 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
              style={{
                background: 'rgba(34,197,94,0.06)',
                border: '1.5px solid rgba(34,197,94,0.2)',
              }}
            >
              <FileText className="w-7 h-7 text-green-400" />
              <span className="text-[10px] font-bold text-green-400 uppercase tracking-wide">
                Patient Report Form
              </span>
              <span className="text-[9px] text-white/35 px-4 text-center">
                Full clinical PRF
              </span>
            </button>
          </div>
        </div>
      </div>

      <MdtParacetamolModal
        open={showParacetamolModal}
        onClose={() => setShowParacetamolModal(false)}
        onSubmit={handleParacetamolSubmit}
        submitting={submittingParacetamol}
      />
    </div>
  );
}
